import { z } from 'zod';

import type { ValidationIssue } from '../core/issues.js';
import type { ActivityCheckContext, ActivityDefinition } from './types.js';

import { trimmed } from './_checks.js';
import { draftFields } from './_shared.js';
import { activityIssue } from './types.js';
import { parseVideoUrl } from './videoUrl.js';

const issue = activityIssue('video');

/**
 * The links the player can embed, as `parseVideoUrl` reads them; `LINK_FORMS` lists the accepted
 * forms. Anything else renders the player's "unsupported source" card instead of a video.
 */
const isEmbeddableVideoUrl = (url: string): boolean => parseVideoUrl(url) !== null;

/** Every link form `isEmbeddableVideoUrl` accepts, written once and shown to authors when a link is rejected. */
export const LINK_FORMS = 'use a Vimeo link (vimeo.com/<id>, vimeo.com/<id>/<hash> or player.vimeo.com/video/<id>) '
  + 'or a YouTube link (youtube.com/watch?v=<id>, youtu.be/<id>, youtube.com/shorts/<id>, youtube.com/embed/<id> or youtube.com/live/<id>)';

const urlIssues = (setup: Record<string, unknown>, ctx: ActivityCheckContext): ValidationIssue[] => {
  const url = trimmed(setup.url);
  const path = [...ctx.path, 'setup', 'url'];
  if (!url) {
    return [issue('noUrl', 'No video URL is written, so there is nothing to play.', path, ctx)];
  }
  if (!isEmbeddableVideoUrl(url)) {
    return [issue('unsupportedSource', `'${url}' is not a video link the player can embed: ${LINK_FORMS}.`, path, ctx, url)];
  }
  return [];
};

/** The caption names the video for the play button, the player frame and the transcript. */
const captionIssues = (setup: Record<string, unknown>, ctx: ActivityCheckContext): ValidationIssue[] =>
  trimmed(setup.caption)
    ? []
    : [issue('noCaption', 'No caption is written. Screen readers and the transcript have nothing to call the video.', [...ctx.path, 'setup', 'caption'], ctx)];

/**
 * The setup has no `provider` (the link decides it) and no `requireWatched` (watch-gating is out
 * of scope); a node that carries either key passes through the loose schema and is ignored. See
 * KUI's `VideoActivity` for the reasoning.
 */
export const video: ActivityDefinition = {
  type: 'video',
  summary: 'A captioned Vimeo or YouTube video, finishing with one outcome when the player taps Continue.',
  setup: z.looseObject({
    ...draftFields,
    url: z.string().optional(),
    caption: z.string().optional(),
  }),
  check: (setup, ctx) => [...urlIssues(setup, ctx), ...captionIssues(setup, ctx)],
};
